import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/api/client';
import { clientKeys } from '@/api/hooks/useClients';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// Client Hub Stats
export interface ClientHubStats {
  deliveredPallets: number;
  recoveredPallets: number;
  recoveredFromClient: number;
  recoveredFromCedis: number;
  transferredPallets: number;
  /** Confirmed losses deducted from `balance`. */
  lostPallets: number;
  balance: number;
  pendingRequisitions: number;
  activeRecoveries: number;
  pendingDamages: number;
}

// Transfer Item
export interface Transfer {
  id: number;
  folio: string;
  clientId: number;
  clientName: string;
  locationName: string;
  amount: number;
  date: string;
  createdDate: string;
  imageCount?: number;
}

export interface TransferImage {
  mediaId: number;
  name: string | null;
  mediaPath: string;
  mediaType: string | null;
  imageUrl: string;
}

// Activity Item
export interface ClientActivityItem {
  id: number;
  type: 'requisition' | 'recovery' | 'damage';
  action: string;
  folio: string;
  description: string;
  date: string;
  status?: string;
}

// Trend Data Point
export interface ClientTrendPoint {
  month: string;
  deliveries: number;
  recoveries: number;
}

// Query keys
export const clientHubKeys = {
  all: ['clientHub'] as const,
  stats: (clientId: number) => [...clientHubKeys.all, 'stats', clientId] as const,
  activity: (clientId: number) => [...clientHubKeys.all, 'activity', clientId] as const,
  trends: (clientId: number) => [...clientHubKeys.all, 'trends', clientId] as const,
  transfers: (clientId: number) => [...clientHubKeys.all, 'transfers', clientId] as const,
  allTransfers: ['clientHub', 'allTransfers'] as const,
};

// Get aggregated stats for client dashboard
export function useClientHubStats(clientId: number, enabled = true) {
  return useQuery({
    queryKey: clientHubKeys.stats(clientId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ClientHubStats>>(
        `/api/client/${clientId}/stats`
      );
      return response.data.data;
    },
    enabled: enabled && clientId > 0,
  });
}

// Get transfers for a specific client
export function useClientTransfers(clientId: number, enabled = true) {
  return useQuery({
    queryKey: clientHubKeys.transfers(clientId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<Transfer[]>>(
        `/api/client/${clientId}/transfers`
      );
      return response.data.data;
    },
    enabled: enabled && clientId > 0,
  });
}

// Get all transfers (global, optionally filtered by client and date window).
// startDate/endDate are ISO strings; omit both for the full lifetime set —
// the endpoint applies no default window. The dates are part of the query key
// so a caller paging through windows actually refetches instead of rendering
// the first-fetched window forever.
export function useAllTransfers(
  clientId?: number,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: [...clientHubKeys.allTransfers, clientId, startDate, endDate] as const,
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (clientId) params.clientId = clientId;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get<ApiResponse<Transfer[]>>(
        '/api/client/transfers',
        { params: Object.keys(params).length > 0 ? params : undefined }
      );
      return response.data.data;
    },
    // Date bounds are part of the key, so stepping the scheduler's window
    // changes the key. Keep the previous window rendered while the next one
    // loads instead of flashing a skeleton over the whole card.
    placeholderData: keepPreviousData,
  });
}

// Get recent activity for overview section
export function useClientActivity(clientId: number, limit = 10, enabled = true) {
  return useQuery({
    queryKey: clientHubKeys.activity(clientId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ClientActivityItem[]>>(
        `/api/client/${clientId}/activity`,
        { params: { limit } }
      );
      return response.data.data;
    },
    enabled: enabled && clientId > 0,
  });
}

// Get trend data for chart (last 6 months)
export function useClientTrends(clientId: number, enabled = true) {
  return useQuery({
    queryKey: clientHubKeys.trends(clientId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ClientTrendPoint[]>>(
        `/api/client/${clientId}/trends`
      );
      return response.data.data;
    },
    enabled: enabled && clientId > 0,
  });
}

// Get evidence images for a single transfer (notification)
export function useTransferImages(notificationId: number | null) {
  return useQuery({
    queryKey: ['transferImages', notificationId],
    queryFn: async () => {
      const response = await api.get<TransferImage[]>(
        `/api/notification/${notificationId}/images`
      );
      return response.data;
    },
    enabled: notificationId !== null && notificationId > 0,
  });
}

// Save (edit) a transfer for a specific client
export interface TransferSaveRequest {
  notificationId: number;
  folio: string;
  locationName: string;
  amount: number;
  date: string;
}

export function useSaveTransfer(clientId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TransferSaveRequest) => {
      const response = await api.post<ApiResponse<number>>(
        `/api/client/${clientId}/transfers/save`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientHubKeys.transfers(clientId) });
      queryClient.invalidateQueries({ queryKey: clientHubKeys.allTransfers, exact: false });
      // Transfers are the `transferred` term of the client balance. Without
      // this the Delivery Scheduler's "saldo" keeps the pre-edit value while
      // the transfer bars beside it already show the new one — `clientKeys`
      // is a separate namespace that clientHubKeys.all cannot match.
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Delete (soft-delete) a transfer
export function useDeleteTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await api.post<ApiResponse<unknown>>(
        '/api/client/transfers/delete',
        { notificationId }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientHubKeys.allTransfers, exact: false });
      queryClient.invalidateQueries({ queryKey: clientHubKeys.all });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Impersonate client admin response
interface ImpersonateResponse {
  serviceToken: string;
  user: {
    email: string;
    userName: string;
    name: string;
    clientId: string;
  };
}

// Impersonate client admin - generates a JWT token for the client's admin user
export function useImpersonateClient() {
  return useMutation({
    mutationFn: async (clientId: number) => {
      const response = await api.post<ImpersonateResponse>(
        `/api/security/impersonate-client/${clientId}`
      );
      return response.data;
    },
  });
}

// Impersonate a specific user - generates a JWT token for the given userId
export function useImpersonateUser() {
  return useMutation({
    mutationFn: async (userId: number) => {
      const response = await api.post<ImpersonateResponse>(
        `/api/security/impersonate-user/${userId}`
      );
      return response.data;
    },
  });
}
